import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { List, LayoutGrid } from "lucide-react";
import { SnippetHtml } from "@/components/SnippetHtml";
import PlatformHeader from "@/components/PlatformHeader";
import SuiteFooter from "@/components/SuiteFooter";

/**
 * HustleFinder — public search UI over the HustleGeo master POI DB.
 *
 * Deployed at hustlefind.au (single-purpose PWA that the v1 iOS wrapper
 * points at) but also mounted inside the main app at /finder for
 * preview + debugging.
 *
 * Deliberately minimal: no auth, no UGC, no analytics tags in the UI.
 * The whole point is a squeaky-clean binary for App Review.
 */

const API = (process.env.REACT_APP_BACKEND_URL || "") + "/api/geo";

/** Fire a semantic analytics event. Works with both GTM (via
 *  window.dataLayer) and GA4 gtag (which itself pushes to dataLayer),
 *  so callers only need one code path. Silent no-op if analytics
 *  hasn't loaded yet (which is fine — early events are lost anyway). */
function track(event, params) {
  try {
    if (typeof window === "undefined") return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event, ...(params || {}) });
  } catch (_e) { /* ignore */ }
}

/**
 * Category tiles. ``types`` is an array so a single tile can span
 * multiple master POI types (e.g. "Kid Zone" bundles playgrounds, dog
 * parks and parks). ``route`` overrides navigation for tiles that
 * point to a dedicated page (Puzzles) rather than the search grid.
 */
const CATEGORIES = [
  { key: "toilet",            labelKey: "finder.cat.toilet",     icon: "🚻",
     defaultLabel: "Toilets",             types: ["toilet"] },
  { key: "drinking_fountain", labelKey: "finder.cat.fountain",   icon: "🚰",
     defaultLabel: "Drinking Fountains",  types: ["drinking_fountain"] },
  { key: "transport_stop",    labelKey: "finder.cat.transport",  icon: "🚌",
     defaultLabel: "Transport",           types: ["transport_stop"] },
  { key: "park",              labelKey: "finder.cat.park",       icon: "🌳",
     defaultLabel: "Parks",               types: ["park"] },
  { key: "medical",           labelKey: "finder.cat.medical",    icon: "🏥",
     defaultLabel: "Medical",             types: ["medical"] },
  { key: "school",            labelKey: "finder.cat.school",     icon: "🏫",
     defaultLabel: "Schools",             types: ["school"] },
  { key: "library",           labelKey: "finder.cat.library",    icon: "📚",
     defaultLabel: "Libraries",           types: ["library"] },
  { key: "service_station",   labelKey: "finder.cat.petrol",     icon: "⛽",
     defaultLabel: "Petrol",              types: ["service_station"] },
  { key: "kid_zone",          labelKey: "finder.cat.kidzone",    icon: "🧒",
     defaultLabel: "Kid Zone",
     types: ["park", "library"],
     subtype_hint: "playground,dog_park,sports_field",
     route: "/finder/kids" },
  { key: "puzzles",           labelKey: "finder.cat.puzzles",    icon: "🧩",
     defaultLabel: "Puzzles",
     route: "/finder/puzzles" },
  { key: "roadtrip",          labelKey: "finder.cat.roadtrip",   icon: "🛣️",
     defaultLabel: "Road trip",
     route: "/roadtrip" },
];

// ─── iter248: HustleFind's inline LanguagePicker has been removed ───
// Language switching is now provided globally by the black top strip
// (<GoogleTranslateBar/>) — mounted at the app shell level. Keeping
// a second picker inside the module header was creating a duplicate
// UI control and clashing with the "one header everywhere" rule.
//
// Historical note: this file previously contained a bespoke
// <LanguagePicker/> component that hit i18n.changeLanguage directly.
// It was removed together with the module-owned <header> below when
// PlatformHeader took over the cream-strip title bar.

function CategoryChips({ selected, onSelect }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  return (
    <div
      className="flex flex-wrap justify-center gap-2 pb-1"
      data-testid="finder-categories"
    >
      {CATEGORIES.map((c) => (
        <button
          key={c.key}
          onClick={() => {
            if (c.route) {
              track("finder_tile_click", { finder_tile: c.key });
              navigate(c.route);
              return;
            }
            const next = selected === c.key ? null : c.key;
            if (next) {
              track("finder_category_click", { finder_category: next });
            }
            onSelect(next);
          }}
          data-testid={`finder-cat-${c.key}`}
          className={`shrink-0 px-2 py-0.5 rounded-full text-[11px] border transition
            ${selected === c.key
              ? "bg-emerald-600 text-white border-emerald-600"
              : "bg-white text-slate-700 border-slate-300 hover:border-emerald-500"}`}
        >
          <span className="mr-0.5">{c.icon}</span>
          {t(c.labelKey, {defaultValue: c.defaultLabel})}
        </button>
      ))}
    </div>
  );
}

function ResultRow({ poi, onSelect }) {
  const { t } = useTranslation();
  const a = poi.address || {};
  const line2 = [a.suburb, a.state, a.postcode].filter(Boolean).join(" · ");
  const dist =
    poi.distance_m != null
      ? poi.distance_m < 1000
        ? `${Math.round(poi.distance_m)} m`
        : `${(poi.distance_m / 1000).toFixed(1)} km`
      : null;
  // Translate OSM type/subtype tags. Falls back to the humanised
  // English (underscore → space) if the current locale doesn't have
  // that specific type in the dictionary — so nothing renders raw.
  const typeLabel = (raw) => {
    if (!raw) return "";
    const key = `finder.poi_type.${raw}`;
    const fb  = String(raw).replace(/_/g, " ");
    return t(key, { defaultValue: fb });
  };
  return (
    <button
      onClick={() => onSelect(poi)}
      data-testid={`finder-result-${poi.id}`}
      className="w-full text-left px-4 py-3 border-b border-slate-100
                 hover:bg-emerald-50 transition"
    >
      <div className="flex items-baseline justify-between gap-3">
        <div className="min-w-0">
          <div className="font-medium text-slate-900 truncate">{poi.name}</div>
          <div className="text-xs text-slate-500">
            {typeLabel(poi.type)}
            {poi.subtype ? ` · ${typeLabel(poi.subtype)}` : ""}
            {line2 ? ` · ${line2}` : ""}
          </div>
        </div>
        {dist && (
          <span className="text-xs font-semibold text-emerald-700 shrink-0">
            {dist}
          </span>
        )}
      </div>
    </button>
  );
}

/** Suburb typeahead — hits /api/geo/suburbs and returns a list of
 *  {suburb,state,postcode,count} matches. Used above the main POI
 *  search box to disambiguate common names like "Darwin". */
function SuburbFilter({ value, onSelect, onClear }) {
  const { t } = useTranslation();
  const [q, setQ]           = useState("");
  const [items, setItems]   = useState([]);
  const [open, setOpen]     = useState(false);
  const [hi, setHi]         = useState(0);
  const debRef              = useRef(null);
  const wrapRef             = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  useEffect(() => {
    clearTimeout(debRef.current);
    if (q.trim().length < 2) { setItems([]); return; }
    debRef.current = setTimeout(async () => {
      try {
        const r = await fetch(`${API}/suburbs?q=${encodeURIComponent(q.trim())}&limit=12`);
        if (!r.ok) throw new Error();
        const j = await r.json();
        setItems(j.items || []);
        setHi(0);
      } catch (_e) { setItems([]); }
    }, 160);
    return () => clearTimeout(debRef.current);
  }, [q]);

  const pick = (it) => {
    onSelect(it);
    setQ("");
    setItems([]);
    setOpen(false);
    track("finder_suburb_pick", {
      finder_suburb:   it.suburb,
      finder_state:    it.state,
      finder_postcode: it.postcode,
    });
  };

  // Active-filter pill mode
  if (value) {
    return (
      <div className="flex items-center justify-center gap-2 pt-1">
        <span
          data-testid="finder-suburb-pill"
          className="inline-flex items-center gap-2 bg-emerald-50 text-emerald-800
                       border border-emerald-200 rounded-full px-3 py-1 text-xs
                       font-semibold"
        >
          <span>📍</span>
          <span>{value.suburb} · {value.state} {value.postcode}</span>
          <button
            data-testid="finder-suburb-clear"
            onClick={onClear}
            aria-label={t("finder.suburb.clear", { defaultValue: "Clear suburb filter" })}
            className="text-emerald-700 hover:text-emerald-900 leading-none"
          >×</button>
        </span>
      </div>
    );
  }

  return (
    <div ref={wrapRef} className="relative">
      <input
        data-testid="finder-suburb-input"
        type="search"
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (!open || !items.length) return;
          if (e.key === "ArrowDown") { e.preventDefault(); setHi(i => Math.min(i + 1, items.length - 1)); }
          else if (e.key === "ArrowUp") { e.preventDefault(); setHi(i => Math.max(i - 1, 0)); }
          else if (e.key === "Enter") { e.preventDefault(); pick(items[hi]); }
          else if (e.key === "Escape") setOpen(false);
        }}
        placeholder={t("finder.suburb.placeholder", {
          defaultValue: "Filter by suburb (e.g. Darwin, NT)…",
        })}
        className="w-full pl-4 pr-3 py-2 rounded-lg border border-slate-200
                     bg-slate-50 text-sm text-slate-800 placeholder-slate-400
                     focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100
                     focus:bg-white focus:outline-none transition"
      />
      {open && items.length > 0 && (
        <div
          data-testid="finder-suburb-menu"
          className="absolute z-30 left-0 right-0 mt-1 max-h-72 overflow-y-auto
                       bg-white border border-slate-200 rounded-lg shadow-lg"
        >
          {items.map((it, idx) => (
            <button
              key={`${it.suburb}-${it.postcode}`}
              onMouseEnter={() => setHi(idx)}
              onClick={() => pick(it)}
              data-testid={`finder-suburb-option-${it.postcode}`}
              className={`w-full flex items-baseline justify-between gap-2
                            text-left px-3 py-2 text-sm
                            ${idx === hi ? "bg-emerald-50" : "bg-white hover:bg-emerald-50"}`}
            >
              <span className="text-slate-900 truncate">
                <span className="font-semibold">{it.suburb}</span>
                <span className="text-slate-400"> | {it.state} {it.postcode}</span>
              </span>
              <span className="text-[10px] uppercase tracking-wider text-slate-400 shrink-0">
                {it.count.toLocaleString()}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Trivago hotel search form. Opens Trivago in a new tab pre-filled
 *  with the query, check-in tomorrow + 1 night by default. The Awin
 *  affiliate ID (env `AWIN_TRIVAGO_MID`) can be appended server-side
 *  later for commission — for now this is a clean deep-link. */
function HotelSearchForm({ defaultQuery = "" }) {
  const { t } = useTranslation();
  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const dayAfter = new Date(today);
  dayAfter.setDate(dayAfter.getDate() + 2);
  const fmt = (d) => d.toISOString().slice(0, 10);
  const [q, setQ]         = useState(defaultQuery);
  const [checkIn, setIn]  = useState(fmt(tomorrow));
  const [checkOut, setOut] = useState(fmt(dayAfter));
  const [adults, setAd]   = useState(2);

  useEffect(() => { if (defaultQuery) setQ(defaultQuery); }, [defaultQuery]);

  const submit = (e) => {
    e.preventDefault();
    const query = q.trim();
    if (!query) return;
    const url = new URL("https://www.trivago.com.au/en-AU/srl");
    url.searchParams.set("search", query);
    if (checkIn)  url.searchParams.set("aDateRange[arr]", checkIn);
    if (checkOut) url.searchParams.set("aDateRange[dep]", checkOut);
    if (adults)   url.searchParams.set("aRooms", `1-${adults}-0`);
    track("finder_hotel_search", {
      finder_hotel_query: query,
      finder_hotel_checkin: checkIn,
      finder_hotel_checkout: checkOut,
      finder_hotel_adults: adults,
    });
    window.open(url.toString(), "_blank", "noopener,noreferrer");
  };

  return (
    <form
      onSubmit={submit}
      data-testid="finder-hotels-form"
      className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-3"
    >
      <label className="col-span-1 sm:col-span-2 text-[11px] font-semibold text-slate-700">
        {t("finder.hotels.where", { defaultValue: "Where" })}
        <input
          data-testid="finder-hotels-where"
          type="text"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t("finder.hotels.where_ph", {
            defaultValue: "City or suburb (e.g. Byron Bay, NSW)",
          })}
          className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 bg-white text-sm text-slate-800 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 focus:outline-none"
        />
      </label>
      <label className="text-[11px] font-semibold text-slate-700">
        {t("finder.hotels.checkin", { defaultValue: "Check-in" })}
        <input
          data-testid="finder-hotels-checkin"
          type="date"
          value={checkIn}
          onChange={(e) => setIn(e.target.value)}
          className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 bg-white text-sm text-slate-800 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 focus:outline-none"
        />
      </label>
      <label className="text-[11px] font-semibold text-slate-700">
        {t("finder.hotels.checkout", { defaultValue: "Check-out" })}
        <input
          data-testid="finder-hotels-checkout"
          type="date"
          value={checkOut}
          onChange={(e) => setOut(e.target.value)}
          className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 bg-white text-sm text-slate-800 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 focus:outline-none"
        />
      </label>
      <label className="text-[11px] font-semibold text-slate-700">
        {t("finder.hotels.adults", { defaultValue: "Adults" })}
        <input
          data-testid="finder-hotels-adults"
          type="number" min={1} max={8}
          value={adults}
          onChange={(e) => setAd(Math.max(1, Number(e.target.value) || 1))}
          className="mt-1 w-full px-3 py-2 rounded-lg border border-slate-300 bg-white text-sm text-slate-800 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200 focus:outline-none"
        />
      </label>
      <div className="col-span-1 sm:col-span-2 flex items-center justify-between gap-3 pt-1">
        <p className="text-[11px] text-slate-400 italic">
          {t("finder.hotels.disclosure", {
            defaultValue: "Opens Trivago — we may earn commission on bookings.",
          })}
        </p>
        <button
          data-testid="finder-hotels-submit"
          type="submit"
          disabled={!q.trim()}
          className="h-9 px-5 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-[12px] font-bold disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {t("finder.hotels.search", { defaultValue: "Search hotels" })}
        </button>
      </div>
    </form>
  );
}

export default function HustleFinderPage() {
  const navigate            = useNavigate();
  const { t }               = useTranslation();
  const [q, setQ]           = useState("");
  const [suburbFilter, setSuburbFilter] = useState(null); // {suburb, state, postcode}
  const [cat, setCat]       = useState(() => {
    // Pre-select category from `?cat=xxx` on first mount (used by
    // /finder/kids → "Find kid-friendly places nearby" deep link).
    if (typeof window === "undefined") return null;
    const key = new URLSearchParams(window.location.search).get("cat");
    return CATEGORIES.some((c) => c.key === key) ? key : null;
  });
  const [results, setRes]   = useState([]);
  const [loading, setLoad]  = useState(false);
  const [near, setNear]     = useState(null); // {lat, lon, source?: "ip"|"gps"}
  const [err, setErr]       = useState(null);
  const [viewMode, setViewMode] = useState(() => {
    // Snippet-first "Cards" view uses <SnippetHtml poi_card /> per row
    // — same server-rendered card the WP [hustlesnippet] shortcode and
    // Directory Grid use. Compact "Rows" view keeps the tap-through
    // list for fast scanning. Persist across page reloads.
    if (typeof window === "undefined") return "rows";
    return window.localStorage.getItem("finder_view_mode") || "rows";
  });
  const debounceRef         = useRef(null);

  useEffect(() => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem("finder_view_mode", viewMode);
    }
  }, [viewMode]);

  // ── Approximate IP-based geolocation on first mount ──
  // Runs once. If the user later hits "Near me" we upgrade to the
  // browser's precise GPS coords (see findNearMe below). This means
  // clicking a category tab returns local results by default rather
  // than the DB's natural order (which happens to be NSW-heavy).
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        // NOTE: ``API`` already includes "/api/geo" so we don't add it again.
        const r = await fetch(`${API}/whereami`);
        if (!r.ok) return;
        const j = await r.json();
        if (!alive) return;
        if (typeof j.lat === "number" && typeof j.lon === "number") {
          setNear((cur) => cur || {
            lat: j.lat, lon: j.lon,
            city: j.city, state: j.state,
            source: "ip",
          });
        }
      } catch { /* silent — the page still works without a soft location */ }
    })();
    return () => { alive = false; };
  }, []);

  // Set browser tab title + meta description for SEO/PWA.
  useEffect(() => {
    document.title = t("finder.meta.title",
      { defaultValue: "HustleFinder — Find toilets, parks, transport & more, Australia-wide" });
    const m = document.querySelector('meta[name="description"]');
    const content = t("finder.meta.desc", { defaultValue:
      "Search Australia's public places instantly — public toilets, " +
      "drinking fountains, playgrounds, parks, bus stops, train " +
      "stations, hospitals, libraries and more. Free, no login, real data." });
    if (m) m.setAttribute("content", content);
    else {
      const el = document.createElement("meta");
      el.setAttribute("name", "description");
      el.setAttribute("content", content);
      document.head.appendChild(el);
    }
  }, [t]);

  // Debounced search
  useEffect(() => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const catDef = CATEGORIES.find((c) => c.key === cat);
      const params = new URLSearchParams();
      if (q.trim()) params.set("q", q.trim());
      if (catDef && catDef.types) params.set("types", catDef.types.join(","));
      if (suburbFilter) {
        params.set("suburb",   suburbFilter.suburb);
        params.set("postcode", suburbFilter.postcode);
      }
      if (near && near.source === "gps") {
        // Only precise GPS coords may filter the search — the soft
        // IP-based fallback (source="ip") is frequently WAY off
        // (mobile carriers CGNAT users to Sydney or Melbourne PoPs
        // even when they're standing in Oxley QLD). Passing that as
        // an implicit filter caused "Toilets near me" to return only
        // NSW results and name-searches like "oxley" to return
        // nothing because the 50 km ``$near`` window landed on
        // Sydney Harbour instead of Brisbane's south side.
        params.set("lat", near.lat);
        params.set("lon", near.lon);
      }
      params.set("limit", "25");
      // Soft IP-based location alone shouldn't fire a search — the user
      // still needs to pick a category, type a query, or hit "Near me"
      // for precise GPS. Otherwise we'd query on page load for no reason.
      const hasIntent = q.trim() || cat || suburbFilter || (near && near.source === "gps");
      if (!hasIntent) {
        setRes([]); return;
      }
      setLoad(true); setErr(null);
      try {
        const r = await fetch(`${API}/search?${params}`);
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        const pois = j.pois || [];
        setRes(pois);
        track("finder_search", {
          finder_query:    q.trim() || null,
          finder_category: cat || null,
          finder_suburb:   suburbFilter ? suburbFilter.suburb : null,
          finder_postcode: suburbFilter ? suburbFilter.postcode : null,
          finder_near_me:  !!near,
          finder_results:  pois.length,
        });
      } catch (e) {
        setErr(e.message);
        track("finder_search_error", { finder_message: e.message });
      } finally {
        setLoad(false);
      }
    }, 200);
    return () => clearTimeout(debounceRef.current);
  }, [q, cat, near, suburbFilter]);

  const findNearMe = () => {
    if (!navigator.geolocation) {
      setErr("Your browser doesn't support location. Try Chrome or Safari.");
      return;
    }
    track("finder_near_me_click");
    setLoad(true);
    // Mobile-friendly geolocation:
    //  - enableHighAccuracy: true so iOS/Android use the actual GPS chip.
    //    Setting `false` on mobile can silently skip GPS and time out.
    //  - timeout: 20s to allow cold GPS locks (indoor / slow satellite).
    //  - maximumAge: 60s so re-clicks reuse a fresh fix instead of
    //    hammering the sensor.
    navigator.geolocation.getCurrentPosition(
      (p) => {
        setNear({ lat: p.coords.latitude, lon: p.coords.longitude,
                   source: "gps" });
        setErr("");
        track("finder_near_me_granted");
      },
      (e) => {
        // Map raw browser codes to friendly, mobile-aware guidance.
        //   1 = PERMISSION_DENIED, 2 = POSITION_UNAVAILABLE, 3 = TIMEOUT
        const msg =
          e.code === 1
            ? "Location permission blocked. On iPhone: Settings › Safari › Location › Allow. On Android: tap the padlock next to the URL › Permissions › Location."
            : e.code === 2
            ? "Couldn't get a location fix. If you're indoors, try moving near a window or step outside."
            : e.code === 3
            ? "Location took too long. Try again with GPS switched on, or use the suburb filter."
            : "Location error: " + (e.message || "unknown");
        setErr(msg);
        setLoad(false);
        track("finder_near_me_denied",
              { finder_message: e.message, finder_code: e.code });
      },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 60000 }
    );
  };

  const clearNear = () => setNear(null);

  const placeholder = useMemo(() => {
    const c = CATEGORIES.find((x) => x.key === cat);
    if (c) {
      return t("finder.search_in", {
        defaultValue: "Search {{cat}}…",
        cat: t(c.labelKey, { defaultValue: c.key }).toLowerCase(),
      });
    }
    return t("finder.search_placeholder", {
      defaultValue: "Search places, streets or suburbs…",
    });
  }, [cat, t]);

  return (
    <div data-testid="finder-page"
         className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* iter248: The old bespoke HustleFinder <header> (brand mark +
       *  inline LanguagePicker + tight single-row search) has been
       *  removed. PlatformHeader now renders THE single global cream
       *  #FDFCF7 strip carrying the module page-name token — same as
       *  Roadtrip Planner and every other module host. Language,
       *  Emergency, Theme and Modules are all in the black top bar
       *  above (GoogleTranslateBar).
       *
       *  The search input, Near-me button, suburb filter and category
       *  chips have been demoted to normal page content (below) — they
       *  are functional widgets, not header chrome, and the user
       *  explicitly asked for the header to be just the token strip. */}
      <PlatformHeader
        showCart={false}
        showBookCta={false}
        showAccount={false}
        nav={[]}
      />

      {/* iter254: Free-games quick-launch band. The two flagship free
       *  experiences on Finder — Kids Games and Sudoku — are surfaced
       *  ABOVE the search block so first-time visitors (and search
       *  engines) hit them without scrolling past the POI grid. */}
      <section
        data-testid="finder-free-games-band"
        className="max-w-2xl mx-auto px-3 pt-4"
      >
        <div className="grid grid-cols-2 gap-3">
          <a
            href="/finder/kids"
            data-testid="finder-quicklaunch-kids"
            onClick={() => track("finder_quicklaunch_click", { target: "kids" })}
            className="group flex items-center gap-3 rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-4 hover:shadow-md hover:border-emerald-400 transition"
          >
            <span className="text-3xl leading-none" aria-hidden="true">🧒</span>
            <span className="flex flex-col leading-tight min-w-0">
              <span className="text-[10px] uppercase tracking-widest text-emerald-700 font-black">
                Free · No login
              </span>
              <span className="text-sm font-bold text-slate-900 group-hover:text-emerald-700">
                Kids Games
              </span>
              <span className="text-[11px] text-slate-500 truncate">
                Mazes · Colouring · Jigsaw
              </span>
            </span>
          </a>
          <a
            href="/finder/puzzles/sudoku"
            data-testid="finder-quicklaunch-sudoku"
            onClick={() => track("finder_quicklaunch_click", { target: "sudoku" })}
            className="group flex items-center gap-3 rounded-2xl border border-emerald-200 bg-gradient-to-br from-amber-50 to-white p-4 hover:shadow-md hover:border-amber-400 transition"
          >
            <span className="text-3xl leading-none" aria-hidden="true">🧩</span>
            <span className="flex flex-col leading-tight min-w-0">
              <span className="text-[10px] uppercase tracking-widest text-amber-700 font-black">
                Free · No login
              </span>
              <span className="text-sm font-bold text-slate-900 group-hover:text-amber-700">
                Sudoku
              </span>
              <span className="text-[11px] text-slate-500 truncate">
                Easy · Medium · Hard
              </span>
            </span>
          </a>
        </div>
      </section>

      <section
        data-testid="finder-search-block"
        className="max-w-2xl mx-auto px-3 pt-3 pb-2"
      >
        <div className="relative">
          <input
            data-testid="finder-search-input"
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={placeholder}
            className="w-full pl-3 pr-20 h-10 rounded-lg border border-slate-300
                       focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200
                       focus:outline-none text-slate-800 text-sm"
          />
          <button
            onClick={(near && near.source === "gps") ? clearNear : findNearMe}
            data-testid="finder-near-me-btn"
            title={near && near.source === "ip"
              ? "Approximate location from your IP — click for precise GPS"
              : undefined}
            className={`absolute right-1 top-1/2 -translate-y-1/2 px-2 py-1
                        rounded-md text-[11px] font-semibold transition
                        ${near && near.source === "gps"
                          ? "bg-emerald-600 text-white"
                          : near && near.source === "ip"
                            ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                            : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
          >
            {near && near.source === "gps" ? "📍 " : ""}
            {near && near.source === "ip" && near.state
              ? `📌 ${near.state}`
              : t("finder.near_me", { defaultValue: "Near me" })}
          </button>
        </div>
        <div className="mt-2">
          <SuburbFilter
            value={suburbFilter}
            onSelect={setSuburbFilter}
            onClear={() => setSuburbFilter(null)}
          />
        </div>
        <div className="mt-2">
          <CategoryChips selected={cat} onSelect={setCat} />
        </div>
      </section>

      <main className="max-w-2xl mx-auto px-4 py-4">
        {loading && (
          <div data-testid="finder-loading"
               className="text-center text-sm text-slate-500 py-6">
            {t("finder.searching", { defaultValue: "Searching…" })}
          </div>
        )}
        {err && (
          <div data-testid="finder-error"
               className="bg-red-50 border border-red-200 text-red-700 rounded-lg
                          px-3 py-2 text-sm my-3">
            {err}
          </div>
        )}
        {!loading && results.length === 0 && (q || cat || near) && (
          <div data-testid="finder-empty"
               className="text-center text-sm text-slate-500 py-10">
            {t("finder.empty", { defaultValue:
              "No matches. Try another category, spelling, or Near me." })}
          </div>
        )}
        {!loading && !q && !cat && !near && (
          <div className="text-center text-sm text-slate-500 py-10">
            {t("finder.hint", { defaultValue:
              "Start typing above, tap a category, or use \"Near me\"." })}
          </div>
        )}
        <div
          data-testid="finder-results"
          className="mt-2"
        >
          {results.length > 0 && (
            <div className="flex items-center justify-between mb-2 px-1">
              <span className="text-xs text-slate-500 tabular-nums"
                     data-testid="finder-results-count">
                {results.length}&nbsp;result{results.length === 1 ? "" : "s"}
              </span>
              <div
                className="inline-flex rounded-full border border-slate-200 bg-white p-0.5"
                role="group"
                aria-label="View mode"
              >
                <button
                  onClick={() => setViewMode("rows")}
                  data-testid="finder-view-rows"
                  aria-pressed={viewMode === "rows"}
                  className={`h-7 px-3 rounded-full text-[11px] font-semibold inline-flex items-center gap-1 transition
                    ${viewMode === "rows"
                      ? "bg-emerald-600 text-white"
                      : "text-slate-600 hover:bg-slate-50"}`}
                >
                  <List size={12}/> Rows
                </button>
                <button
                  onClick={() => setViewMode("cards")}
                  data-testid="finder-view-cards"
                  aria-pressed={viewMode === "cards"}
                  className={`h-7 px-3 rounded-full text-[11px] font-semibold inline-flex items-center gap-1 transition
                    ${viewMode === "cards"
                      ? "bg-emerald-600 text-white"
                      : "text-slate-600 hover:bg-slate-50"}`}
                >
                  <LayoutGrid size={12}/> Cards
                </button>
              </div>
            </div>
          )}
          {viewMode === "cards" ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3"
                  data-testid="finder-results-cards">
              {results.map((p, idx) => (
                <div
                  key={p.id}
                  data-testid={`finder-card-${p.id}`}
                  onClick={() => {
                    track("finder_result_click", {
                      finder_poi_id:   p.id,
                      finder_poi_type: p.type,
                      finder_position: idx + 1,
                    });
                    navigate(`/finder/poi/${p.id}`);
                  }}
                  className="cursor-pointer rounded-xl border border-slate-200 bg-white p-3 hover:shadow-md hover:border-emerald-300 transition"
                >
                  <SnippetHtml
                    scope="global"
                    name="poi_card"
                    params={{ poi_id: p.id }}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              {results.map((p, idx) => (
                <ResultRow
                  key={p.id}
                  poi={p}
                  onSelect={(poi) => {
                    track("finder_result_click", {
                      finder_poi_id:   poi.id,
                      finder_poi_type: poi.type,
                      finder_position: idx + 1,
                    });
                    navigate(`/finder/poi/${poi.id}`);
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </main>

      {/* Find a hotel — Trivago blocks iframe embedding, so this is a
       * search form that opens Trivago in a new tab with the query
       * pre-filled. Awin affiliate id (AFFID env) is appended when
       * configured server-side; otherwise it opens direct. */}
      <section
        data-testid="finder-hotels-panel"
        className="max-w-2xl mx-auto px-4 pb-10"
      >
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
          <div className="px-5 py-4 border-b border-slate-100">
            <h2 className="text-sm font-bold text-slate-900">
              {t("finder.hotels.title", { defaultValue: "Find a hotel nearby" })}
            </h2>
            <p className="text-[11px] text-slate-500 mt-0.5">
              {t("finder.hotels.sub", { defaultValue:
                "Compares 200+ booking sites in one search — powered by Trivago." })}
            </p>
          </div>
          <HotelSearchForm defaultQuery={
            suburbFilter?.suburb || (near?.city ? `${near.city}, ${near.state || "AU"}` : "")
          }/>
        </div>
      </section>

      <footer
        data-testid="finder-footer-links"
        className="max-w-2xl mx-auto px-4 py-3 text-[10px] text-slate-400 space-y-1"
      >
        <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1">
          <a href="/legal/privacy" target="_blank" rel="noopener noreferrer"
             data-testid="finder-footer-privacy" className="hover:text-slate-600">
            {t("finder.footer.privacy", { defaultValue: "Privacy" })}
          </a>
          <span aria-hidden="true">·</span>
          <a href="/legal/terms" target="_blank" rel="noopener noreferrer"
             data-testid="finder-footer-terms" className="hover:text-slate-600">
            {t("finder.footer.terms", { defaultValue: "Terms" })}
          </a>
          <span aria-hidden="true">·</span>
          <a href="/legal/disclaimer" target="_blank" rel="noopener noreferrer"
             data-testid="finder-footer-disclaimer" className="hover:text-slate-600">
            {t("finder.footer.disclaimer", { defaultValue: "Disclaimer" })}
          </a>
          <span aria-hidden="true">·</span>
          <a href="/support" target="_blank" rel="noopener noreferrer"
             data-testid="finder-footer-support" className="hover:text-slate-600">
            {t("finder.footer.support", { defaultValue: "Support" })}
          </a>
        </div>
        <div className="text-center text-[9px] text-slate-400 leading-snug">
          {t("finder.footer.trivago", { defaultValue:
            "Hotel search powered by Trivago — we may earn a commission on bookings." })}
        </div>
        <div className="text-center text-[9px] text-slate-400">
          © {new Date().getFullYear()} HustleSuite · LOCAL HUSTLE · ABN&nbsp;16&nbsp;160&nbsp;565&nbsp;775
        </div>
      </footer>
      <SuiteFooter />
    </div>
  );
}
